import './xeroPurchaseOrderForm.scss';

import { ScrollableArea } from '@approvalmax/ui';
import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { constants, selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useIsOcrUploadButtonVisible } from 'pages/requestForm/hooks/useIsOcrUploadButtonVisible';
import { useOcrStatus } from 'pages/requestForm/hooks/useOcrStatus';
import { FC } from 'react';
import bemFactory from 'react-bem-factory';
import { useUserBetaFeatures } from 'shared/hooks';

import { uploadAttachments } from '../../../actions';
import { AttachmentsSection, NoteForApproversSection, RequesterInstructionSection } from '../../../components';
import { EmailToSupplierMessages } from '../../../components/EmailToSupplierForm/EmailToSupplierForm.types';
import FileDropProvider from '../../../components/FileDropProvider';
import SendToSupplierFileDropArea from '../../../components/SendToSupplierFileDropArea';
import useShowDropArea from '../../../hooks/useShowDropArea';
import { getContext } from '../../../selectors/pageSelectors';
import { getFieldsApprovalPermissionBySystemPurpose, getRequestEditMode } from '../../../selectors/requestSelectors';
import { getXeroContext } from '../../../selectors/xero';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { OcrUploadButton } from '../OcrUploadButton/OcrUploadButton';
import { XeroDeliveryDetailsSection } from '../XeroDeliveryDetailsSection/XeroDeliveryDetailsSection';
import { XeroDeliveryDetailsSection as XeroDeliveryDetailsSectionOld } from '../XeroDeliveryDetailsSectionOld/XeroDeliveryDetailsSection';
import XeroEmailToSupplierSection from '../XeroEmailToSupplierSection/XeroEmailToSupplierSection';
import XeroLineItemsSection from '../XeroLineItemsSection/XeroLineItemsSection';
import { XeroPurchaseOrderFieldsSection } from '../XeroPurchaseOrderFieldsSection/XeroPurchaseOrderFieldsSection';
import { messages } from './XeroPurchaseOrderForm.messages';

const { xeroConstants } = constants;
const bem = bemFactory.block('reqf-xero-purchase-order-form');

const labels: Partial<EmailToSupplierMessages> = {
    formTitle: messages.formTitle,
    toggleLabel: messages.toggleLabel,
    attachmentsLabel: messages.attachmentsLabel,
    attachmentsNote: messages.attachmentsNote,
};

interface XeroPurchaseOrderFormProps {
    className?: string;
    request: domain.XeroPoRequest;
}

const placeholderList = [
    domain.SupplierEmailReplacements.poNumber,
    domain.SupplierEmailReplacements.totalAmount,
    domain.SupplierEmailReplacements.supplierName,
    domain.SupplierEmailReplacements.companyName,
    domain.SupplierEmailReplacements.contactPersonName,
];

export const XeroPurchaseOrderForm: FC<XeroPurchaseOrderFormProps> = (props) => {
    const { className, request } = props;

    const dispatch = useDispatch();
    const context = useSelector(getContext);
    const contextXero = useSelector(getXeroContext);
    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const [attachmentFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroAttachment])
    );

    const showDropArea = useShowDropArea();

    const isEmailToVendorEnabled =
        contextXero?.supplierEmailSettings?.state !== domain.TemplateSettingsEmailToSupplierState.Disabled;
    const isEmailToVendorReadonly =
        request.details.emailToSupplier !== null &&
        request.details.emailToSupplier.emailHasToBeSent &&
        contextXero?.supplierEmailSettings?.state === domain.TemplateSettingsEmailToSupplierState.Disabled;
    const showEmailToSupplier = isEmailToVendorEnabled || isEmailToVendorReadonly;

    const isOcrUploadButtonVisible = useIsOcrUploadButtonVisible(company, request);

    useOcrStatus(request);

    const { isBelgradeUIFilesUploadSection, isBelgradeUIEmailToSupplier, isBelgradeUIDeliveryAddressSection } =
        useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection && !isBelgradeUIEmailToSupplier}
            wrapperIf={(children) => (
                <FileDropProvider
                    className={bem.add(className)()}
                    onUpload={(files, attachmentKind) => dispatch(uploadAttachments({ files, attachmentKind }))}
                    maxSize={context.attachmentMaxSize}
                    allowedFileExtensions={xeroConstants.XERO_ALLOWED_FILE_EXTENSIONS}
                >
                    {children}

                    {showDropArea && (
                        <SendToSupplierFileDropArea isSupplierAreaActive={request.details.sendToSupplier} />
                    )}
                </FileDropProvider>
            )}
        >
            <div className={bem('toolbar')}>
                <div className={bem('toolbar-header')}>
                    <div className={bem('toolbar-header-text')}>{messages.headerText}</div>

                    <ActionsBlock />
                </div>

                <XeroPurchaseOrderFieldsSection request={request} />
            </div>

            <ScrollableArea className={bem('content')}>
                <div className={bem('content-inner')}>
                    <RequesterInstructionSection instructionText={context.requesterInstruction} />

                    <ConditionWrapper
                        condition={isBelgradeUIFilesUploadSection}
                        wrapperIf={(children) => (
                            <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                                {children}

                                <AttachmentsSection
                                    request={request}
                                    editMode={editMode}
                                    maxFilesInList={9}
                                    extensions={xeroConstants.xeroAllowedFileExtensions}
                                    readOnly={attachmentFieldPermission.disabled}
                                    required={attachmentFieldPermission.required}
                                />
                            </Grid>
                        )}
                    >
                        {(request.origin === domain.RequestOrigin.ApprovalMax ||
                            request.origin === domain.RequestOrigin.PublicApi) && <NoteForApproversSection />}
                    </ConditionWrapper>

                    <XeroLineItemsSection request={request} />

                    {!isBelgradeUIFilesUploadSection && (
                        <>
                            <FilesSection
                                editMode={editMode}
                                maxFilesCount={9}
                                readonly={attachmentFieldPermission.disabled}
                                required={attachmentFieldPermission.required}
                            />

                            {isOcrUploadButtonVisible && <OcrUploadButton request={request} />}
                        </>
                    )}

                    {isBelgradeUIDeliveryAddressSection ? (
                        <XeroDeliveryDetailsSection request={request} />
                    ) : (
                        <XeroDeliveryDetailsSectionOld request={request} />
                    )}

                    {showEmailToSupplier && (
                        <XeroEmailToSupplierSection
                            request={request}
                            labels={labels}
                            placeholderList={placeholderList}
                            isReadonly={isEmailToVendorReadonly}
                        />
                    )}
                </div>
            </ScrollableArea>
        </ConditionWrapper>
    );
};

XeroPurchaseOrderForm.displayName = 'XeroPurchaseOrderForm';
